import { useCallback, type ReactNode } from 'react'
import type { VaultDocumentCategory } from '@deepguide-ai/types'
import { Editor } from '@tiptap/react'
import { VAULT_CATEGORY_LABELS } from '@/lib/vault-document.constants'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { PolicyContentViewer } from '@/components/screenata/PolicyContentViewer'

/**
 * Reusable edit surface for a Vault document — title, category,
 * description, and TipTap body. Owned by the parent (controlled
 * inputs + editor ref callback) so it can drop into either:
 *
 *   - the existing detail route (`documents_.$docId.tsx`)
 *   - the agent draft overlay (`VaultDocumentDraftOverlay`)
 *
 * Read-only mode is supported by passing `readOnly` — used for the
 * detail route's view state. Save/Cancel actions live in the parent
 * (page header / overlay header), not here.
 */
export interface VaultDocumentEditorProps {
  title: string
  category: VaultDocumentCategory
  description: string
  contentMarkdown?: string | null
  tiptapJson?: unknown | null
  /**
   * Stable key — when this changes, TipTap re-initializes from the
   * latest content. Set per-doc-version (e.g. `vault-doc-${id}`) so
   * switching between docs / draft sessions reloads cleanly.
   */
  contentKey: string
  readOnly?: boolean
  /**
   * Hide title/category/description form rows (read-only views often
   * render the title in a page header instead).
   */
  hideMetaForm?: boolean
  hideContentEditor?: boolean
  onTitleChange?: (next: string) => void
  onCategoryChange?: (next: VaultDocumentCategory) => void
  onDescriptionChange?: (next: string) => void
  /**
   * Receive the TipTap editor instance so the parent can pull
   * `editor.getJSON()` / markdown when the user clicks Save.
   */
  onEditorReady?: (editor: Editor) => void
  emptyTitle?: string
  emptyDescription?: string
  variant?: 'card' | 'settings' | 'split'
  className?: string
  editorToolbar?: ReactNode
}

export function VaultDocumentEditor({
  title,
  category,
  description,
  contentMarkdown,
  tiptapJson,
  contentKey,
  readOnly = false,
  hideMetaForm = false,
  hideContentEditor = false,
  onTitleChange,
  onCategoryChange,
  onDescriptionChange,
  onEditorReady,
  emptyTitle = 'Start writing...',
  emptyDescription = 'Use the editor to add content to this document.',
  variant = 'card',
  className,
  editorToolbar,
}: VaultDocumentEditorProps) {
  const handleCreate = useCallback(
    ({ editor }: { editor: Editor }) => {
      onEditorReady?.(editor)
    },
    [onEditorReady]
  )

  if (variant === 'settings') {
    return (
      <div className={className ?? 'flex w-full flex-col gap-6'}>
        {!hideMetaForm && (
          <section className='rounded-sm border border-border bg-card px-5 py-5'>
            <h2 className='font-heading text-[14px] font-semibold text-text-secondary'>
              Draft details
            </h2>
            <div className='mt-5 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_220px]'>
              <div className='flex min-w-0 flex-col gap-1.5'>
                <Label className='font-body text-[11px] font-medium text-text-subtle'>
                  Title
                </Label>
                <Input
                  value={title}
                  onChange={(e) => onTitleChange?.(e.target.value)}
                  disabled={readOnly}
                  className='h-9 rounded-sm border-border-divider bg-card text-[14px] font-medium text-text-secondary'
                  placeholder='Document title'
                />
              </div>
              <div className='flex min-w-0 flex-col gap-1.5'>
                <Label className='font-body text-[11px] font-medium text-text-subtle'>
                  Category
                </Label>
                <Select
                  value={category}
                  onValueChange={(v) =>
                    onCategoryChange?.(v as VaultDocumentCategory)
                  }
                  disabled={readOnly}
                >
                  <SelectTrigger
                    fullWidth
                    className='h-9 rounded-sm border-border-divider bg-card text-[12px]'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(VAULT_CATEGORY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className='mt-4 flex flex-col gap-1.5'>
              <Label className='font-body text-[11px] font-medium text-text-subtle'>
                Description
              </Label>
              <Textarea
                value={description}
                onChange={(e) => onDescriptionChange?.(e.target.value)}
                disabled={readOnly}
                className='min-h-[84px] resize-y rounded-sm border-border-divider bg-card text-[13px] leading-[1.5] text-text-secondary'
                placeholder='Brief description'
              />
            </div>
          </section>
        )}
        <section className='rounded-sm border border-border bg-card px-5 py-5'>
          <h2 className='font-heading text-[14px] font-semibold text-text-secondary'>
            Document content
          </h2>
          {!readOnly && (
            <p className='font-body mt-1 text-[12px] text-text-placeholder'>
              Click any section to edit. Your changes are kept when you save.
            </p>
          )}
          <div className='mt-5 border-t border-border px-10 py-8'>
            <PolicyContentViewer
              contentMarkdown={contentMarkdown}
              plateContent={tiptapJson}
              readOnly={readOnly}
              contentKey={contentKey}
              onCreate={handleCreate}
              className='min-h-[400px]'
              emptyTitle={emptyTitle}
              emptyDescription={emptyDescription}
            />
          </div>
        </section>
      </div>
    )
  }

  if (variant === 'split') {
    return (
      <div
        className={className ?? 'mx-auto flex w-full max-w-5xl flex-col gap-5'}
      >
        {!hideMetaForm && (
          <div className='rounded-sm border border-border bg-card px-5 py-4'>
            <div className='space-y-1'>
              <Label className='text-[11px] font-medium text-text-subtle'>
                Title
              </Label>
              <Input
                value={title}
                onChange={(e) => onTitleChange?.(e.target.value)}
                disabled={readOnly}
                className='h-9 rounded-sm border-border-divider text-[14px] font-medium'
                placeholder='Document title'
              />
            </div>
            <div className='mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2'>
              <div className='flex min-w-0 flex-col gap-1.5'>
                <Label className='text-[11px] font-medium text-text-subtle'>
                  Category
                </Label>
                <Select
                  value={category}
                  onValueChange={(v) =>
                    onCategoryChange?.(v as VaultDocumentCategory)
                  }
                  disabled={readOnly}
                >
                  <SelectTrigger
                    fullWidth
                    className='h-9 rounded-sm border-border-divider text-[12px]'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(VAULT_CATEGORY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='flex min-w-0 flex-col gap-1.5'>
                <Label className='text-[11px] font-medium text-text-subtle'>
                  Description
                </Label>
                <Input
                  value={description}
                  onChange={(e) => onDescriptionChange?.(e.target.value)}
                  disabled={readOnly}
                  className='h-9 rounded-sm border-border-divider text-[12px]'
                  placeholder='Brief description'
                />
              </div>
            </div>
          </div>
        )}
        {!hideContentEditor && <div className='rounded-sm border border-border bg-card'>
          {editorToolbar && (
            <div className='border-border flex h-9 items-center border-b'>
              {editorToolbar}
            </div>
          )}
          <div className='px-15 py-8'>
            <PolicyContentViewer
              contentMarkdown={contentMarkdown}
              plateContent={tiptapJson}
              readOnly={readOnly}
              contentKey={contentKey}
              onCreate={handleCreate}
              className='min-h-[400px]'
              emptyTitle={emptyTitle}
              emptyDescription={emptyDescription}
            />
          </div>
        </div>}
      </div>
    )
  }

  return (
    <div
      className={
        className ??
        'mx-auto w-full max-w-5xl rounded-sm border border-border bg-card'
      }
    >
      {!hideMetaForm && (
        <>
          <div className='space-y-1 px-5 py-3'>
            <Label className='text-[11px] font-medium text-text-subtle'>
              Title
            </Label>
            <Input
              value={title}
              onChange={(e) => onTitleChange?.(e.target.value)}
              disabled={readOnly}
              className='h-9 rounded-sm border-border-divider text-[14px] font-medium'
              placeholder='Document title'
            />
          </div>
          <div className='px-5 py-3'>
            <div className='mb-3 flex gap-3'>
              <div className='flex flex-1 flex-col gap-1.5'>
                <Label className='text-[11px] font-medium text-text-subtle'>
                  Category
                </Label>
                <Select
                  value={category}
                  onValueChange={(v) =>
                    onCategoryChange?.(v as VaultDocumentCategory)
                  }
                  disabled={readOnly}
                >
                  <SelectTrigger
                    fullWidth
                    className='h-8 rounded-sm border-border-divider text-[12px]'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(VAULT_CATEGORY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='flex flex-1 flex-col gap-1.5'>
                <Label className='text-[11px] font-medium text-text-subtle'>
                  Description
                </Label>
                <Input
                  value={description}
                  onChange={(e) => onDescriptionChange?.(e.target.value)}
                  disabled={readOnly}
                  className='h-8 rounded-sm border-border-divider text-[12px]'
                  placeholder='Brief description'
                />
              </div>
            </div>
          </div>
        </>
      )}
      <div className='border-t border-border px-15 py-4'>
        <PolicyContentViewer
          contentMarkdown={contentMarkdown}
          plateContent={tiptapJson}
          readOnly={readOnly}
          contentKey={contentKey}
          onCreate={handleCreate}
          className='min-h-[400px]'
          emptyTitle={emptyTitle}
          emptyDescription={emptyDescription}
        />
      </div>
    </div>
  )
}

/**
 * Pull current state from a TipTap editor instance — JSON for
 * persistence, markdown for search/export compatibility. Mirrors the
 * extraction in the existing detail route.
 */
export function readEditorContent(editor: Editor | null) {
  if (!editor) return { tiptapJson: null as unknown, content: '' as string }
  const tiptapJson = editor.getJSON() as unknown
  const markdown = editor.storage.markdown?.getMarkdown?.()
  const content = typeof markdown === 'string' ? markdown : editor.getText()
  return { tiptapJson, content }
}
